/**
 * Family Selector Component
 *
 * Simple chip-based selector for product families.
 * Similar design to the Application Type selector (Sol/Murs).
 */

import React from 'react';
import { CircleCheck, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { ProductFamily } from '../../../../../common/models/product-family.model';
import { useResolveTranslation } from '../../../../../common/hooks/use-resolve-translation.hook';

interface FamilySelectorProps {
  /** List of available families for the vendor */
  families: ProductFamily[];
  /** Currently selected family ID */
  selectedFamilyId: string | null;
  /** Callback when a family is selected */
  onFamilySelect: (familyId: string) => void;
  /** Loading state */
  loading?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

export const FamilySelector: React.FC<FamilySelectorProps> = ({
  families,
  selectedFamilyId,
  onFamilySelect,
  loading = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { rt } = useResolveTranslation();

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (families.length === 0) {
    return (
      <p className="text-sm italic text-muted-foreground">
        {t('PAGES.NEW_PRODUCT.V3.SETUP.NO_FAMILIES')}
      </p>
    );
  }

  // Don't show selector if only one family
  if (families.length === 1) {
    return (
      <div className="inline-flex items-center gap-1.5 border bg-muted px-4 py-2">
        <CircleCheck className="size-4 text-foreground" />
        <span className="text-sm font-medium text-foreground">{rt(families[0].name)}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {families.map((family) => {
        const isSelected = family._id === selectedFamilyId;
        return (
          <div
            key={family._id}
            onClick={() => !disabled && onFamilySelect(family._id)}
            className={cn(
              'inline-flex items-center gap-1.5 border px-4 py-2 transition-colors',
              isSelected ? 'border-foreground bg-muted' : 'bg-background hover:bg-muted',
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
            )}
          >
            {isSelected && <CircleCheck className="size-4 text-foreground" />}
            <span className="text-sm font-medium text-foreground">{rt(family.name)}</span>
          </div>
        );
      })}
    </div>
  );
};

export default FamilySelector;
